import { useState, useEffect, useContext, useMemo } from "react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { Search } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";

interface TaskSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTaskId: number | null;
  onTaskSelect: (taskId: number) => void;
  editorInstance?: unknown;
}

const SpotIncentiveTaskSelector = ({ open, onOpenChange, selectedTaskId, onTaskSelect, editorInstance }: TaskSelectorProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(selectedTaskId);

  // Sync local state with prop when dialog opens
  useEffect(() => {
    if (open) {
      setLocalSelectedId(selectedTaskId);
    }
  }, [open, selectedTaskId]);

  const tasks = useMemo(() => project?.extraTaskStore?.query((record: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return !record.isPhantom && (canShowArchivedRecords || !record.archived);
  }, true) || [], [project]);

  const filteredTasks = useMemo(() => tasks.filter((task: any) => // eslint-disable-line @typescript-eslint/no-explicit-any
    (task.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  ), [tasks, searchQuery]);

  const handleTaskClick = (taskId: number) => {
    setLocalSelectedId(taskId);
  };

  const handleDone = () => {
    if (localSelectedId) {
      onTaskSelect(localSelectedId);
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const MODAL_SIZE_CONFIG = {
    width: "min(90vw, 32rem)",
    height: "min(90vh, 34rem)",
    minWidth: "min(90vw, 28rem)",
    minHeight: "min(90vh, 34rem)",
    maxWidth: "min(90vw, 32rem)",
    maxHeight: "min(90vh, 36rem)"
  };

  if (!open) return null;

  return (
      <BaseModal
        title="Select Task"
        description="Choose a task to assign spot incentives"
        className="resize-none"
        onClose={() => onOpenChange(false)}
        size={MODAL_SIZE_CONFIG}
        editorInstance={editorInstance}
        record={{}}
        headerIconCls=""
        modal={false}
        version=""
        swivuAssistanceConfig={null}
        hideMenu={true}
        footerRenderer={() => (
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleCancel} className="text-sm">
              Cancel
            </Button>
            <Button onClick={handleDone} disabled={!localSelectedId} className="text-sm">
              Done
            </Button>
          </div>
        )}
    >
      <BaseModalBody>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter task..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm placeholder:text-sm"
            />
          </div>

          <ScrollArea className="h-64">
            {filteredTasks.length > 0 ? (
              <div className="space-y-3">
                {filteredTasks.map((task: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                  const isSelected = localSelectedId === task.id;
                  return (
                    <div
                      key={task.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-primary/10 border-primary/20 ring-1 ring-primary/20'
                          : 'hover:bg-secondary/50'
                      }`}
                      onClick={() => handleTaskClick(task.id)}
                    >
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${task.archived ? 'line-through' : ''} ${
                          isSelected ? 'text-primary' : ''
                        }`}>
                          {task.name || "N/A"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-muted-foreground text-center text-sm">
                No tasks found. Please create a task first.
              </p>
            ) : (
              <p className="text-muted-foreground text-center text-sm">
                No tasks found matching your search.
              </p>
            )}
          </ScrollArea>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};

export default SpotIncentiveTaskSelector;
